// The numbers behind the pricing engine page's fee sheet, laid out like page 2
// of the CFPB Loan Estimate ("Closing Cost Details"). Display only: every
// amount comes from the calculator; nothing here re-prices anything.
//
// What the calculator sends for one rate (GOCostCalculator
// calculation/reCalcLodestar.go, reCalcTotalClosingCost):
//   - closing_costs_detail: this rate's own lines -- broker origination fee
//     (borrower paid), lender origination fee and "x.xxx% of Loan Amount
//     (Points)" in A, upfront mortgage insurance premium in B, prepaid
//     interest in F;
//   - appliedClosingCostsDetail (once per response): the company's configured
//     or Lodestar third-party fees, the same for every rate;
//   - lender_credit: positive = the borrower pays points (already the points
//     line in A), negative = a lender credit, which the Loan Estimate shows on
//     its own "Lender Credits" line under J;
//   - closing_cost = A..H + the lender credit, the "Closing cost" column.
// So J here is computed the Loan Estimate way (D + I + Lender Credits) and
// checked against closing_cost; on the 2,038 dev rates of 2026-11-04 they
// were equal to the cent. If a future response breaks that, the difference
// is shown as its own line instead of being hidden.

const SECTIONS = [
  ["A", "Origination Charges", "originationCharges"],
  ["B", "Services You Cannot Shop For", "canNotShopServices"],
  ["C", "Services You Can Shop For", "canShopServices"],
  ["E", "Taxes and Other Government Fees", "taxesAndOtherGovernmentFees"],
  ["F", "Prepaids", "prepaids"],
  ["G", "Initial Escrow Payment at Closing", "initialEscrowPaymentsAtClosing"],
  ["H", "Other", "other"],
];

const POINTS_LINE = /^\s*([\d.]+)\s*%\s*of loan amount\s*\(points\)\s*$/i;
const PREPAID_INTEREST = /^\s*(?:period|prepaid)\s+interest\b/i;
const PREPAID_INTEREST_PARTS = /\(\s*\$?([\d,.]+)\s*per day for\s*(\d+)\s*days?\s*@\s*([\d.]+)\s*%\s*\)/i;
export const UPFRONT_MIP = "Upfront Mortgage Insurance Premium";

// Work in cents so a column of two-decimal amounts adds up exactly.
const cents = (value) => Math.round((Number(value) || 0) * 100);
const dollars = (value) => value / 100;

const validName = (item) => item && typeof item.serviceName === "string" && item.serviceName.trim() !== "";

function mergedItems(own, applied, field) {
  const a = Array.isArray(own && own[field]) ? own[field] : [];
  const b = Array.isArray(applied && applied[field]) ? applied[field] : [];
  return [...a, ...b].filter(validName).map((item) => ({ name: item.serviceName.trim(), cents: cents(item.estimateAmount) }));
}

// "Period interest ($68.06 per day for 26 days @ 6.125%)" ->
// "Prepaid Interest ($68.06 per day for 26 days @ 6.125%)", the Loan
// Estimate's wording.
export function prepaidInterestLabel(name) {
  if (!PREPAID_INTEREST.test(name)) return name;
  const parts = name.match(PREPAID_INTEREST_PARTS);
  if (!parts) return name.replace(PREPAID_INTEREST, "Prepaid Interest");
  const perDay = Number(parts[1].replace(/,/g, ""));
  const perDayText = perDay.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `Prepaid Interest ($${perDayText} per day for ${parts[2]} days @ ${Number(parts[3]).toFixed(3)}%)`;
}

const isPurchase = (params) => String(params.purpose || params.loanPurpose || "").toLowerCase() === "purchase";

function isSecondLien(params) {
  const wire = params.pricingRequest || {};
  const program = String(params.loanProgram || wire.loanProgram || "").toLowerCase();
  const lien = String(params.lienPosition || wire.lienPosition || "").toLowerCase();
  return ["heloc", "heloan", "secondmortgage"].includes(program) || ["second", "2nd"].includes(lien);
}

// record: the rate as the result table holds it (row.raw: the product's
// fields, the rate's fields and loanAmount). appliedDetail: the response's
// appliedClosingCostsDetail. params: the search body the results came from.
export function buildFeeSheet(record, appliedDetail, params) {
  const rate = record || {};
  const sp = params || {};
  const own = rate.closing_costs_detail || (rate.estimatedclosingcosts && rate.estimatedclosingcosts.closingCostsDetail) || {};

  let points = null;
  const sections = {};
  SECTIONS.forEach(([letter, label, field]) => {
    const items = [];
    mergedItems(own, appliedDetail, field).forEach((item) => {
      const pointsMatch = field === "originationCharges" && item.name.match(POINTS_LINE);
      if (pointsMatch) {
        points = { percent: pointsMatch[1], cents: (points ? points.cents : 0) + item.cents };
        return;
      }
      // The calculator always sends the lender fee and the upfront premium,
      // $0 when there is none; a $0 line on a Loan Estimate is noise.
      if (item.cents === 0) return;
      items.push({ ...item, name: field === "prepaids" ? prepaidInterestLabel(item.name) : item.name });
    });
    const total = items.reduce((sum, item) => sum + item.cents, 0) + (letter === "A" && points ? points.cents : 0);
    sections[letter] = { letter, label, field, items, cents: total };
  });

  const sum = (letters) => letters.split("").reduce((total, letter) => total + sections[letter].cents, 0);
  const loanCosts = sum("ABC");
  const otherCosts = sum("EFGH");
  const lenderCredit = Math.min(cents(rate.lender_credit), 0);
  const computedJ = loanCosts + otherCosts + lenderCredit;
  const reported = rate.closing_cost === undefined || rate.closing_cost === null ? null : cents(rate.closing_cost);
  const totalClosingCosts = reported === null ? computedJ : reported;

  const ufmip = sections.B.items.filter((item) => item.name === UPFRONT_MIP).reduce((total, item) => total + item.cents, 0);
  // The form's "UFMIP financed" box is on by default; a saved search without
  // the field is treated the same way, as the price breakdown drawer does.
  const financed = sp.upFrontMIP_Financed === false ? 0 : -ufmip;
  const downPayment = !isSecondLien(sp) && isPurchase(sp) ? cents(sp.downPayment) : 0;
  // The pricing engine page has no deposit, seller credit or other credit
  // inputs; these stay $0 rows so the section reads like the form.
  const cash = {
    totalClosingCosts,
    closingCostsFinanced: financed,
    downPayment,
    deposit: 0,
    fundsForBorrower: 0,
    sellerCredits: 0,
    adjustmentsAndOtherCredits: 0,
  };
  cash.cashToClose = cash.totalClosingCosts + cash.closingCostsFinanced + cash.downPayment
    - cash.deposit + cash.fundsForBorrower - cash.sellerCredits + cash.adjustmentsAndOtherCredits;

  return {
    sections,
    points,
    loanCosts,
    otherCosts,
    lenderCredit,
    totalClosingCosts,
    // Non-zero only if the calculator's closing_cost ever stops being
    // D + I + lender credit; shown on its own line so J still adds up.
    unexplained: totalClosingCosts - computedJ,
    cash,
    rate: Number(rate.base_rate),
    loanAmount: Number(rate.loanAmount) || 0,
  };
}

export const toDollars = dollars;
